import { Outlet } from "react-router-dom";
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import TopBar from "./TopBar";
import BottomNav from "./BottomNav";
import Sidebar from "./Sidebar";
import ScrollToTop from "@/components/ScrollToTop";
import TrialBanner from "@/components/account/TrialBanner";
import TrialWelcomeDialog from "@/components/account/TrialWelcomeDialog";
import { useActiveWorkout } from "@/lib/ActiveWorkoutContext";

const IS_NATIVE =
  Capacitor.isNativePlatform() ||
  (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("nativeView") === "1");

/**
 * JS-level overscroll lock. Runs only inside the Capacitor wrapper.
 * Prevents document rubber-band by blocking touchmove events that would
 * pull the page past its top or bottom edge. Inner scroll containers
 * (modals, dropdowns) are NOT affected because we check the event target
 * chain and only veto when the touch is actually on the document body.
 *
 * This is the same trick the Twitter / Instagram iOS web wrappers use —
 * works without touching native Swift code.
 */
function installOverscrollLock() {
  let lastY = 0;
  const onStart = (e) => {
    lastY = e.touches[0].clientY;
  };
  const onMove = (e) => {
    const y = e.touches[0].clientY;
    const dy = y - lastY;
    lastY = y;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const maxScroll =
      document.documentElement.scrollHeight - window.innerHeight;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop >= maxScroll - 1;
    // Block pull-down at top + pull-up at bottom.
    if ((atTop && dy > 0) || (atBottom && dy < 0)) {
      // Only veto if the touch target isn't inside a separate scroll
      // container (Radix dialog, dropdown, etc).
      let el = e.target;
      while (el && el !== document.body) {
        const cs = window.getComputedStyle(el);
        if (
          (cs.overflowY === "auto" || cs.overflowY === "scroll") &&
          el.scrollHeight > el.clientHeight
        ) {
          return;
        }
        el = el.parentElement;
      }
      e.preventDefault();
    }
  };
  document.addEventListener("touchstart", onStart, { passive: true });
  document.addEventListener("touchmove", onMove, { passive: false });
  return () => {
    document.removeEventListener("touchstart", onStart);
    document.removeEventListener("touchmove", onMove);
  };
}

export default function AppLayout() {
  const { active: activeWorkout } = useActiveWorkout();
  const isFullscreenWorkout = Boolean(activeWorkout?.is_fullscreen);

  useEffect(() => {
    if (!IS_NATIVE) return;
    return installOverscrollLock();
  }, []);

  if (isFullscreenWorkout) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <ScrollToTop />
        <main className="w-full h-full min-h-screen">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <ScrollToTop />
      <Sidebar />
      <div className="md:hidden">
        <TopBar />
      </div>
      <div className="flex flex-1">
        <main
          className="flex-1 md:pt-0 md:pb-0 lg:ml-64 overflow-x-hidden"
          style={{
            // Top padding = compact header content height (3.5rem / 56px) + iOS Dynamic Island
            // safe-area inset. On non-notched devices the env() resolves to
            // 0, falling back to 3.5rem.
            paddingTop: "calc(3.5rem + env(safe-area-inset-top))",
            // Bottom padding must clear the fixed BottomNav, which is
            // `h-16` (64px) + env(safe-area-inset-bottom) (~34px on
            // iPhones with the home indicator). 7rem (112px) + the inset
            // gives the last card on each page a comfortable ~16-30px
            // breathing room above the nav on every iPhone we ship to.
            // Desktop overrides this via `md:pb-0` above.
            paddingBottom: "calc(7rem + env(safe-area-inset-bottom))",
          }}
        >
          <div className="p-4 pt-4 max-w-7xl mx-auto">
            {/* Sticky trial countdown — returns null for non-trial users
                so it doesn't push content down for paid/free members. */}
            <TrialBanner />
            <Outlet />
          </div>
        </main>
      </div>
      <BottomNav />
      {/* One-time welcome modal for fresh trial signups. Self-gates on
          localStorage so it only fires once per user, ever. */}
      <TrialWelcomeDialog />
    </div>
  );
}
